// ---------------------------------------------------------------------------
// Block Definition: bullet_list
//
// List of bullet items with two sub-styles:
//   dot_item  — colored dot + fixed label + right-aligned qualifier
//   event     — optional RAG dot + date + tag badge + headline + evidence
//
// Crystallizer: LLM extraction with per-label rubrics (dot_item) and
// list guidance (event).  Post-processing reorders dot_item items to
// match DSL label order.
// ---------------------------------------------------------------------------

import { BlockRegistry } from "../index.js";

const EVIDENCE_DESC = "Verbatim sentence(s) from the SOURCE that justify this value — quote exactly, do not paraphrase. If combining separate spans, join them with ' … '. null if not directly stated.";

function nullable(type, description, extra = {}) {
  const t = Array.isArray(type) ? type : [type, "null"];
  return { type: t, ...(description ? { description } : {}), ...extra };
}

// ── Sub-type: dot_item (merges dot_qualifier + dot_fact) ────────────────

const dotItemStyle = {
  name: "dot_item",
  label: "Dot + Qualifier",
  description: "Colored dot with fixed label and right-aligned qualifier text.",

  buildDataSchema(block, opts) {
    const hints = opts.hints || [];
    const ownedLabels = new Set(hints.map(h => h.siblingLabel).filter(Boolean));
    const allLabels = (block.items || []).map(it => {
      const lbl = it.label;
      if (typeof lbl === 'string') return lbl;
      return lbl?.value || lbl?.text || null;
    }).filter(Boolean);
    const labels = ownedLabels.size > 0 ? allLabels.filter(l => ownedLabels.has(l)) : allLabels;
    const statusEnum = Object.keys(block.dot_color_map || { green: 1, amber: 1, red: 1 });
    const citeQualifier = (block.items || []).some(it => it.qualifier?.cite);
    const itemProps = {
      label: { type: "string", enum: labels, description: "Exact label from the listed signals — return in the same order." },
      status: { type: "string", enum: statusEnum, description: "Color status for this signal. See per-label rubric in the system prompt." },
      qualifier: nullable("string", "Short qualifier (≤5 words) supporting the status.")
    };
    if (citeQualifier) itemProps.qualifier_evidence = nullable("string", EVIDENCE_DESC);
    return {
      type: "object",
      properties: {
        items: {
          type: "array",
          minItems: labels.length,
          maxItems: labels.length,
          items: { type: "object", properties: itemProps, required: Object.keys(itemProps), additionalProperties: false }
        }
      },
      required: ["items"],
      additionalProperties: false
    };
  },

  buildShapeDescription(block) {
    const examples = (block.items || []).map(it => {
      const label = typeof it.label === 'string' ? it.label : it.label?.value || it.label?.key || "?";
      return `{"label":"${label}","status":"green"|"amber"|"red","qualifier":"<string>"}`;
    });
    return `{ "items": [${examples.join(", ")}] }`;
  },

  buildPromptSupplement(block, hints, dsl) {
    const rubricLines = [];
    let statusLegend = block.status_legend;
    for (const h of hints) {
      if (!h.siblingLabel || !h.prompt) continue;
      rubricLines.push(`- "${h.siblingLabel}" → ${h.key}: ${h.prompt}${h.max_words ? ` (≤${h.max_words} words)` : ""}`);
    }
    if (rubricLines.length === 0) return "";
    let head = "=== PER-LABEL RUBRIC (for the colored-dot signals) ===\n";
    if (statusLegend) {
      head += `Status colors mean: green = ${statusLegend.green}; amber = ${statusLegend.amber}; red = ${statusLegend.red}. Apply consistently. Judge the CURRENT state — a company recovering from past trouble is amber, not red. When evidence is mixed or borderline, choose amber. The qualifier must JUSTIFY its colour: for amber/red name the concern or caveat (not just a positive headline) — e.g. amber 'Stabilising post-recap', not 'Debt cut 60%'.\n`;
    }
    return `\n${head}${rubricLines.join("\n")}\n`;
  },

  postProcess(blockData, blockDef) {
    if (!Array.isArray(blockData.items)) return blockData;
    const order = (blockDef.items || []).map(it => { const lbl = it.label; if (typeof lbl === 'string') return lbl; return lbl?.value; }).filter(Boolean);
    const byLabel = new Map(blockData.items.map(x => [x.label, x]));
    blockData.items = order.map(l => byLabel.get(l)).filter(Boolean);
    return blockData;
  },
};

// ── Sub-type: event ─────────────────────────────────────────────────────

const eventStyle = {
  name: "event",
  label: "Event List",
  description: "Chronological event list with tag badges, dates, headlines, and evidence.",

  buildDataSchema(block, opts) {
    const tpl = block.item_template || {};
    const tagEnum = Object.keys(block.tag_styles || {});
    const dotColorEnum = Object.keys(block.dot_color_map || {});
    // Fixed-badge mode: block.items pre-declares a badge value PER POSITION
    // (e.g. one item per required lens/category) instead of letting the
    // model choose freely across the badge enum. This guarantees coverage
    // by position — the model still writes rich content per item, guided
    // by each item's badge_note (see buildPromptSupplement), but the badge
    // itself is fixed by postProcess, not asked of the model. Opt in via
    // item_template.badge.fixed: true + a non-empty block.items array.
    const declaredItems = block.items || [];
    const fixedBadges = Boolean(tpl.badge?.fixed) && declaredItems.length > 0;
    const limit = fixedBadges ? declaredItems.length : (block.item_limit || 5);
    const arrayDesc = [
      fixedBadges ? `Return EXACTLY ${limit} items, one per the fixed assignment below.` : `Aim for up to ${limit} items.`,
      block.priority_hint || "Most recent first. Balance across tag categories."
    ].filter(Boolean).join(" ");

    // In fixed-badge mode every declared item is guaranteed to genuinely
    // apply (there's no "index doesn't exist" case — the badge itself
    // proves it), so headline/evidence must NOT be nullable there: a
    // nullable string is a standing invitation for the model to return
    // null on a real item (observed happening in practice — see
    // candidate_concept_or_assumptions). In dynamic mode, null still
    // legitimately means "fewer real items than the limit", so it stays
    // nullable there.
    const headlineDesc = [tpl.headline?.prompt, tpl.headline?.max_words ? `Max ${tpl.headline.max_words} words.` : null].filter(Boolean).join(" ");
    const evidenceDesc = [tpl.evidence?.prompt, tpl.evidence?.max_words ? `Max ${tpl.evidence.max_words} words.` : null].filter(Boolean).join(" ");
    const itemProps = {
      headline: fixedBadges ? { type: "string", description: headlineDesc } : nullable("string", headlineDesc),
      evidence: fixedBadges ? { type: "string", description: evidenceDesc } : nullable("string", evidenceDesc)
    };
    const itemRequired = ["headline", "evidence"];

    if (tpl.tag) {
      itemProps.tag = { type: "string", ...(tagEnum.length ? { enum: tagEnum } : {}), description: tpl.tag.prompt || "Event category tag." };
      itemRequired.unshift("tag");
    }
    if (tpl.date) {
      itemProps.date = nullable("string", tpl.date.prompt || `Event date (${tpl.date.format || "Mon YYYY"} format)`);
      const pos = itemRequired.indexOf("headline");
      itemRequired.splice(pos, 0, "date");
    }
    if (tpl.status) {
      itemProps.status = { type: "string", ...(dotColorEnum.length ? { enum: dotColorEnum } : {}), description: tpl.status.prompt || "RAG status." };
      itemRequired.unshift("status");
    }
    if (tpl.badge && !fixedBadges) {
      const badgeEnum = Object.keys(block.badge_styles || {});
      itemProps.badge = { type: "string", ...(badgeEnum.length ? { enum: badgeEnum } : {}), description: tpl.badge.prompt || "Badge value." };
      itemRequired.push("badge");
    } else if (tpl.badge && fixedBadges) {
      // Not required from the model (see buildPromptSupplement — the value
      // is fixed by position), but MUST still be a declared property: items
      // use additionalProperties:false, so postProcess's injected badge
      // would otherwise be a schema violation and could get stripped by
      // any downstream re-validation against this same shape.
      itemProps.badge = nullable("string", "Fixed automatically by position — do not fill this yourself, leave null.");
    }
    if (tpl.signals) {
      itemProps.signals = nullable("string", tpl.signals.prompt || "Comma-separated signal evidence strengths.");
      itemRequired.push("signals");
    }

    return {
      type: "object",
      properties: {
        items: {
          type: "array",
          description: arrayDesc,
          minItems: fixedBadges ? limit : 0,
          maxItems: limit,
          items: { type: "object", properties: itemProps, required: itemRequired, additionalProperties: false }
        }
      },
      required: ["items"],
      additionalProperties: false
    };
  },

  buildShapeDescription(block) {
    const tpl = block.item_template || {};
    const declaredItems = block.items || [];
    if (Boolean(tpl.badge?.fixed) && declaredItems.length > 0) {
      return `{ "items": [${declaredItems.map(() => `{"headline":"<string>","evidence":"<string>"}`).join(", ")}] }  // badge is fixed per position, not returned by you`;
    }
    return `{ "items": [{"tag":"BIZ"|"FIN"|"ESG"|"OPS","date":"Mon YYYY","headline":"<string>","evidence":"<string>"}, ...] }`;
  },

  buildPromptSupplement(block, hints, dsl) {
    const tpl = block.item_template || {};
    const declaredItems = block.items || [];
    if (Boolean(tpl.badge?.fixed) && declaredItems.length > 0) {
      const lines = declaredItems.map((it, i) => `${i + 1}. ${it.badge}${it.badge_note ? ` — ${it.badge_note}` : ""}`);
      return `\n=== FIXED ITEM ASSIGNMENT ===\n[${block.id}] Return EXACTLY ${declaredItems.length} items, IN THIS ORDER. Each item's headline/evidence content must address ONLY the lens/category assigned to its position below — do not restate the label itself, it's applied automatically:\n${lines.join("\n")}\n`;
    }
    const limit = block.item_limit || 5;
    return `\n=== LIST GUIDANCE ===\n[${block.id}] Return ${limit} items — only fewer if the source genuinely has fewer than ${limit} distinct events. ${block.priority_hint || ""}\n`;
  },

  // NOTE: this crystallizer's postProcess hook is NOT called anywhere in
  // viz_dsl_crystallizer.js — same as dot_item's and progress's below. The
  // real fixed-badge application lives inline in viz_dsl_crystallizer.js
  // (search "Fixed-badge event items"), matching how dot_qualifier
  // reordering is ALSO applied inline rather than through this hook.
  postProcess(blockData, blockDef) { return blockData; },
};

// ── Sub-type: progress ──────────────────────────────────────────────────

const progressStyle = {
  name: "progress",
  label: "Progress Bars",
  description: "Horizontal progress bars for percentage metrics. Each item shows a label, a filled bar, and a percentage value.",

  buildDataSchema(block, opts) {
    const declaredItems = block.items || [];
    const labels = declaredItems.map(it => {
      const l = it.label;
      if (typeof l === 'string') return l;
      return l?.value || l?.text || null;
    }).filter(Boolean);

    const labelProp = labels.length > 0
      ? { type: "string", enum: labels, description: "Metric label — match exactly from the declared items list." }
      : { type: "string", description: "Metric label." };

    const itemProps = {
      label: labelProp,
      value: nullable(["number", "null"], "Numeric percentage 0–100. null if not found in source."),
      display_value: nullable("string", "Display string e.g. '87%', '<5%', 'N/A'. null only if value is null.")
    };

    return {
      type: "object",
      properties: {
        items: {
          type: "array",
          minItems: labels.length || 0,
          maxItems: labels.length || 10,
          items: { type: "object", properties: itemProps, required: ["label", "value", "display_value"], additionalProperties: false }
        }
      },
      required: ["items"],
      additionalProperties: false
    };
  },

  buildShapeDescription(block) {
    const examples = (block.items || []).map(it => {
      const label = typeof it.label === 'string' ? it.label : (it.label?.value || "?");
      return `{"label":"${label}","value":87,"display_value":"87%"}`;
    });
    return `{ "items": [${examples.slice(0, 2).join(", ")}${examples.length > 2 ? ", ..." : ""}] }`;
  },

  buildPromptSupplement(block, hints, dsl) {
    const lines = (block.items || []).map(it => {
      const label = typeof it.label === 'string' ? it.label : (it.label?.value || "?");
      const vp = it.value_prompt || `Extract the percentage value for "${label}"`;
      return `- "${label}": ${vp}. Return a number 0–100, or null if not in source.`;
    });
    if (lines.length === 0) return "";
    return `\n=== PROGRESS METRICS TO EXTRACT ===\nFor each metric return a numeric percentage (0–100) and a display_value string. Use null for both if the metric is absent from the source.\n${lines.join("\n")}\n`;
  },

  postProcess(blockData, blockDef) {
    if (!Array.isArray(blockData.items)) return blockData;
    const order = (blockDef.items || []).map(it => {
      const l = it.label;
      return typeof l === 'string' ? l : l?.value;
    }).filter(Boolean);
    if (order.length === 0) return blockData;
    const byLabel = new Map(blockData.items.map(x => [x.label, x]));
    blockData.items = order.map(l => byLabel.get(l)).filter(Boolean);
    return blockData;
  },
};

// ── Main definition ─────────────────────────────────────────────────────

export const bulletListDef = {
  type: "bullet_list",
  label: "Bullet List",
  description: "List of items with tags, dates, status dots, and evidence.",

  subTypes: {
    style: (() => {
      const sr = BlockRegistry.createSubRegistry();
      sr.register(dotItemStyle);
      sr.register(eventStyle);
      sr.register(progressStyle);
      return sr;
    })(),
  },

  // The parent crystallizer delegates to the sub-type registry for
  // style-specific behaviour.
  crystallizer: {
    extractionPhases: ["llm"],

    buildDataSchema(block, opts) {
      const styleReg = bulletListDef.subTypes.style;
      const sub = styleReg.getIfRegistered(block.item_style === "dot_qualifier" || block.item_style === "dot_fact" ? "dot_item" : block.item_style || "event");
      if (!sub) return styleReg.get("event").buildDataSchema(block, opts);
      return sub.buildDataSchema(block, opts);
    },

    buildShapeDescription(block) {
      const styleReg = bulletListDef.subTypes.style;
      const sub = styleReg.getIfRegistered(block.item_style === "dot_qualifier" || block.item_style === "dot_fact" ? "dot_item" : block.item_style || "event");
      if (!sub) return styleReg.get("event").buildShapeDescription(block);
      return sub.buildShapeDescription(block);
    },

    isBlockLevelTarget(block) {
      return !block.item_style || (block.item_style !== "dot_qualifier" && block.item_style !== "dot_fact");
    },
    skipFromPinSchema() { return false; },
    isCodeDerived() { return false; },
    collectHints() { return []; },

    buildPromptSupplement(block, hints, dsl) {
      const styleReg = bulletListDef.subTypes.style;
      const sub = styleReg.getIfRegistered(block.item_style === "dot_qualifier" || block.item_style === "dot_fact" ? "dot_item" : block.item_style || "event");
      if (!sub || !sub.buildPromptSupplement) return "";
      return sub.buildPromptSupplement(block, hints, dsl);
    },

    postProcess(blockData, blockDef) {
      const styleReg = bulletListDef.subTypes.style;
      const sub = styleReg.getIfRegistered(blockDef.item_style === "dot_qualifier" || blockDef.item_style === "dot_fact" ? "dot_item" : blockDef.item_style || "event");
      if (!sub || !sub.postProcess) return blockData;
      return sub.postProcess(blockData, blockDef);
    },
  },

  renderer: null, // wired by ui/src/blockRegistryInit.js

  editor: {
    icon: "list-checks",
    category: "data-visualization",
    defaultTemplate: {
      type: "bullet_list",
      item_style: "dot_qualifier",
      items: [
        { label: { role: "literal", value: "Signal A" } },
        { label: { role: "literal", value: "Signal B" } },
      ],
      dot_color_map: { green: "#2D8659", amber: "#BA7517", red: "#C4463A" },
    },
    configFields: [
      { name: "item_style", type: "select", label: "Style", options: ["dot_qualifier", "event"], defaultValue: "dot_qualifier" },
      { name: "columns", type: "number", label: "Columns", defaultValue: 1 },
      { name: "density", type: "select", label: "Density", options: ["normal", "compact"], defaultValue: "normal" },
      { name: "card_background", type: "color", label: "Card background" },
      { name: "item_limit", type: "number", label: "Max items (event)", defaultValue: 5 },
      { name: "proportion", type: "number", label: "Proportion weight", step: 0.1 },
      { name: "dot_color_map", type: "object", label: "Status colors",
        fields: [
          { name: "green", type: "color", label: "Green (on track)", defaultValue: "#2D8659" },
          { name: "amber", type: "color", label: "Amber (caution)", defaultValue: "#BA7517" },
          { name: "red", type: "color", label: "Red (concern)", defaultValue: "#C4463A" },
          { name: "na", type: "color", label: "Grey (no data)", defaultValue: "#888780" },
        ]
      },
      { name: "missing_data", type: "object", label: "Fallback when pin is empty",
        fields: [
          { name: "status", type: "string", label: "Status key", defaultValue: "na" },
          { name: "qualifier", type: "string", label: "Qualifier text", defaultValue: "No data" },
        ]
      },
      { name: "items", type: "array", label: "Signal items (dot_qualifier)",
        itemSchema: { fields: [
          { name: "label", type: "object", label: "Label",
            fields: [
              { name: "role", type: "select", label: "Role", options: ["literal", "llm_gen"], defaultValue: "literal" },
              { name: "value", type: "string", label: "Literal text" },
            ]
          },
        ]}
      },
    ],
  },
};
